import { CheckIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { TotpSetup } from '@/api'
import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

type TotpSetupDialogProps = {
    setup: TotpSetup | null
    onClose: () => void
    onSubmit: (code: string) => Promise<void>
}

export const TotpSetupDialog = ({ setup, onClose, onSubmit }: TotpSetupDialogProps) => {
    const { t } = useTranslation()

    const codeInputRef = useRef<HTMLInputElement | null>(null)
    const [copied, setCopied] = useState(false)
    const [code, setCode] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)

    if (!setup) {
        return null
    }

    const handleCopy = async () => {
        await navigator.clipboard.writeText(setup.secret)
        setCopied(true)
        window.setTimeout(() => setCopied(false), 2000)
    }

    const handleSubmit = async () => {
        setSubmitting(true)
        try {
            await onSubmit(code)
            setCode('')
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : t('messages.totpVerifyError'))
        } finally {
            setSubmitting(false)
        }
    }

    const handleClose = () => {
        setCode('')
        setLocalError(null)
        onClose()
    }

    return (
        <Dialog
            open={!!setup}
            title={t('mfa.totpModalTitle')}
            onClose={handleClose}
            initialFocusRef={codeInputRef}
            closeLabel={t('common.close')}
            footer={
                <div className="flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-page rounded-lg border px-4 py-2 font-semibold transition"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void handleSubmit()
                        }}
                        disabled={submitting || !code}
                        className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {t('mfa.totpVerify')}
                    </button>
                </div>
            }
        >
            <p className="text-account-secondary text-sm">{t('mfa.totpModalDescription')}</p>

            <div className="flex justify-center">
                <div className="border-account-divider rounded-lg border bg-white p-3">
                    <img src={setup.qrCodeDataUrl} alt="" className="h-40 w-40" />
                </div>
            </div>

            <div className="text-center">
                <div className="bg-account-muted text-account-secondary inline-flex max-w-full items-center gap-2 rounded-lg px-3 py-2 text-sm">
                    <code className="text-account-primary max-w-[220px] overflow-hidden text-ellipsis whitespace-nowrap font-mono tracking-[0.18em]">
                        {setup.secret}
                    </code>
                    <button
                        type="button"
                        onClick={() => {
                            void handleCopy()
                        }}
                        aria-label={copied ? t('mfa.totpCopied') : t('mfa.totpCopy')}
                        title={copied ? t('mfa.totpCopied') : t('mfa.totpCopy')}
                        className={[
                            'hover:bg-account-surface inline-flex items-center rounded-md p-1.5 transition',
                            copied ? 'text-account-success' : 'text-account-secondary hover:text-account-primary',
                        ].join(' ')}
                    >
                        {copied ? <CheckIcon className="h-4 w-4" /> : <DocumentDuplicateIcon className="h-4 w-4" />}
                    </button>
                </div>
            </div>

            <TextField
                inputRef={codeInputRef}
                label={t('mfa.totpCodeLabel')}
                value={code}
                placeholder="000000"
                centered
                onChange={(value) => {
                    setCode(value)
                    setLocalError(null)
                }}
                error={localError}
            />
        </Dialog>
    )
}
